import type {
  PlayerGamifications,
  PlayerProfile,
} from '~/types/player-profile';
import { normalizePlayerVip } from '~/utils/apostatudo/player-vip';

// Contrato do core (snake_case, sem envelope `data` — `$wrap = null` no
// PlayerProfileResource):
// {
//   user_id,
//   level: null | string,
//   vip: null | string,
//   gamifications: { [slug]: { min_level: string, is_eligible: boolean } },
//   support_whatsapp: null | string,
// }
//
// O `vip` era `{ tag, order, color }` e virou a tag pura ('VIP 3') — o
// normalizador aceita as duas formas, ver utils/apostatudo/player-vip.
// `support_whatsapp` acompanhou essa mudança: saiu de dentro do objeto `vip`
// e virou campo irmão, solto na raiz — por isso entra separado aqui também.
//
// `level` é o slug do nível no CRM (a escada da Smartico), que o core expõe
// pro front comparar com o que o widget mostra. Pra um VIP ele é o espelho da
// tag, e quem tem `vip` não compara nada — a escada local é a autoridade; quem
// consome a comparação é a regra de divergência (stores/crm-level-sync.ts).
//
// `gamifications` substituiu o `is_eligible` da raiz (feat/cactus-token-introspect
// no core): a elegibilidade passou a ser POR gamificação, contra o nível CRM
// mínimo que a operação define no backoffice. Slug ausente = desativada.
type RawProfile = {
  user_id?: unknown;
  vip?: unknown;
  support_whatsapp?: unknown;
  level?: unknown;
  gamifications?: unknown;
};

export function mapPlayerProfile(
  raw: RawProfile,
  userId: number,
): PlayerProfile {
  return {
    userId: typeof raw.user_id === 'number' ? raw.user_id : userId,
    // A normalização do `vip` mora num util porque o memo desta aba lê o MESMO
    // formato e também não pode confiar nele — ver utils/apostatudo/player-vip.
    vip: normalizePlayerVip(raw.vip, raw.support_whatsapp),
    // String vazia e tipo inesperado viram `null` ("não sei"), e `null` nunca
    // dispara sync — um payload quebrado não pode virar chamada ao warehouse.
    level: typeof raw.level === 'string' && raw.level ? raw.level : null,
    gamifications: mapGamifications(raw.gamifications),
  };
}

// O mapa por gamificação, com duas camadas de defesa que respondem perguntas
// DIFERENTES:
//
// - O MAPA não é um objeto (ausente, array, string, core antigo sem o campo)
//   → `null`, "não sei". Ninguém é negado por um contrato que não chegou; quem
//   decide é o core, no POST de cada feature.
// - Uma ENTRADA está torta (não é objeto, `is_eligible` não é boolean) → ela é
//   descartada, o que pra quem lê equivale a "slug ausente" = não elegível.
//   Dentro de um mapa válido, erro numa chave não pode virar convite.
//
// `is_eligible` só é `true` quando o core AFIRMA `true`; `min_level` só entra se
// for string não vazia, senão `null` (o veredito fica, a copy perde o nome).
export function mapGamifications(raw: unknown): PlayerGamifications | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

  const gamifications: PlayerGamifications = {};

  for (const [slug, entry] of Object.entries(raw as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue;

    const { min_level, is_eligible } = entry as {
      min_level?: unknown;
      is_eligible?: unknown;
    };

    if (typeof is_eligible !== 'boolean') continue;

    gamifications[slug] = {
      minLevel:
        typeof min_level === 'string' && min_level.trim()
          ? min_level.trim()
          : null,
      isEligible: is_eligible === true,
    };
  }

  return gamifications;
}
